import { ApiResponse, Culture, cultures, ListResult } from "../VolunteersHighlightContext/constants";

export const VOLUNTEERS_EXPERIENCE_LIST_PATH = "/volunteers-experiences";

/* -------------- LIST (response) -------------- */

// The texts come in the culture of the request only; they are empty when the record has no such culture.
export interface VolunteersExperienceListItem {
	id: string;
	isActive: boolean;
	displayOrder: number;
	name: string;
	surname: string;
	description: string | null;
	title: string | null;
	company: string | null;
}

export type VolunteersExperienceListApiResponse = ApiResponse<ListResult<VolunteersExperienceListItem>>;

/* -------------- GET (response) -------------- */

export interface VolunteersExperienceLocalizable {
	id: string;
	culture: string;
	description: string;
	title: string | null;
	company: string | null;
}

export interface VolunteersExperienceDetail {
	id: string;
	isActive: boolean;
	displayOrder: number;
	name: string;
	surname: string;
	// The full CDN address, or null when no picture was uploaded.
	picture: string | null;
	volunteersExperienceLocalizable: VolunteersExperienceLocalizable[];
}

export type VolunteersExperienceGetApiResponse = ApiResponse<VolunteersExperienceDetail>;

/* -------------- ADD (form = request) -------------- */

export interface VolunteersExperienceLocalizableFormData {
	culture: Culture;
	description: string;
	title: string;
	company: string;
}

export interface VolunteersExperienceFormData {
	isActive: boolean;
	displayOrder?: number;
	name: string;
	surname: string;
	picture: File | null;
	volunteersExperienceLocalizable: VolunteersExperienceLocalizableFormData[];
}

export const getDefaultVolunteersExperienceFormData = (): VolunteersExperienceFormData => ({
	isActive: false,
	displayOrder: undefined,
	name: "",
	surname: "",
	picture: null,
	volunteersExperienceLocalizable: cultures.map(culture => ({
		culture,
		description: "",
		title: "",
		company: "",
	})),
});

/* -------------- EDIT (form state, complete) -------------- */

export interface VolunteersExperienceEditLocalizable extends VolunteersExperienceLocalizableFormData {
	// Empty for a culture the record does not have yet.
	localizableId: string;
}

export interface VolunteersExperienceEditFormData {
	experienceId: string;
	isActive: boolean;
	displayOrder?: number;
	name: string;
	surname: string;
	pictureUrl: string;
	picture: File | null;
	volunteersExperienceLocalizable: VolunteersExperienceEditLocalizable[];
}

// Every culture gets a row so the form always shows both sections; a culture missing on the
// server keeps an empty id and is sent as a new localizable only when the user fills it in.
export const toVolunteersExperienceEditFormData = (detail: VolunteersExperienceDetail): VolunteersExperienceEditFormData => ({
	experienceId: detail.id,
	isActive: detail.isActive,
	displayOrder: detail.displayOrder,
	name: detail.name ?? "",
	surname: detail.surname ?? "",
	pictureUrl: detail.picture ?? "",
	picture: null,
	volunteersExperienceLocalizable: cultures.map(culture => {
		const localizable = detail.volunteersExperienceLocalizable.find(item => item.culture === culture);
		return {
			localizableId: localizable?.id ?? "",
			culture,
			description: localizable?.description ?? "",
			title: localizable?.title ?? "",
			company: localizable?.company ?? "",
		};
	}),
});

/* -------------- UPDATE (PATCH request, partial - only changed fields) -------------- */

export interface UpdateVolunteersExperienceLocalizable {
	// The detail response calls this id "id"; it is left out for a culture that does not exist yet.
	volunteersExperienceLocalizableId?: string;
	culture: string;
	description?: string;
	title?: string;
	company?: string;
}

export interface UpdateVolunteersExperienceRequest {
	experienceId: string;
	isActive?: boolean;
	displayOrder?: number | null;
	name?: string;
	surname?: string;
	picture?: File;
	volunteersExperienceLocalizable?: UpdateVolunteersExperienceLocalizable[];
}

/* -------------- Rules (kept in sync with the backend validator) -------------- */

export const VolunteersExperienceRules = {
	nameMaxLength: 100,
	surnameMaxLength: 100,
	titleMaxLength: 100,
	companyMaxLength: 100,
	descriptionMaxLength: 1000,
	// Letters, digits, spaces and . , ; : ! ? ' " ( ) - _ / & % + # @ – —
	textPattern: /^[\p{L}\p{N}\s.,;:!?'"()\-_/&%+#@–—]*$/u,
	pictureMaxFileSize: 5 * 1024 * 1024,
	// The backend also takes WebP but cannot check its file signature yet, so the panel does not offer it.
	pictureMimeTypes: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
} as const;
